import { Component, computed, inject, input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

/** Zoom del embed: nivel barrio, igual que el backoffice. */
const MAPA_ZOOM = 15;

/**
 * Mapa de la sección "Zona" de la ficha pública (Figma 18-851).
 *
 * Mismo mecanismo que la ficha/alta del backoffice (`urlEmbedMapa` de
 * `geocodificacion.service.ts` + `mapa-pin.svg`): embed clásico de Google
 * Maps (`output=embed`, sin API key) centrado en las coordenadas que
 * geocodificó el backend, con el pin propio superpuesto porque ese embed
 * no trae marcador. El iframe es solo visualización (sin pan/zoom): panear
 * movería el mapa bajo el pin fijo y este dejaría de marcar el punto.
 *
 * Reemplaza a la Maps JavaScript API del spec §7.1 (FE-00/FE-02): la key
 * de FP-34 nunca llegó de infra y la ficha no puede depender de ella. La
 * vista de mapa del buscador (`mapa-propiedades.ts`) sí sigue en la JS API:
 * sus pines interactivos, clustering y popups no existen en el embed.
 */
@Component({
  selector: 'app-zona-mapa',
  template: `
    <!-- Coordenadas no finitas (el shape viene de la red) → nada, igual
         que la ficha cuando la propiedad no tiene coordenadas. -->
    @if (mapaSrc(); as src) {
      <div class="zona-mapa">
        <iframe
          title="Mapa de la zona"
          [src]="src"
          loading="lazy"
          tabindex="-1"
        ></iframe>
        <img class="zona-mapa__pin" src="/mapa-pin.svg" alt="" />
      </div>
    }
  `,
  styles: `
    .zona-mapa {
      position: relative;
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid var(--c-neutrals-grey_100);
    }

    iframe {
      display: block;
      width: 100%;
      height: 320px;
      border: 0;
      background: var(--c-neutrals-grey_50);
      // Solo visualización: el pin fijo debe seguir marcando las coordenadas.
      pointer-events: none;
    }

    .zona-mapa__pin {
      position: absolute;
      left: 50%;
      top: 50%;
      width: 45px; // tamaño intrínseco del SVG (45×66): sin achatar
      height: 66px;
      // El vértice del pin (y≈58.5 sobre un viewBox de 66 → 88.7%; el resto
      // es su sombra) cae en el centro exacto del mapa.
      transform: translate(-50%, -88.7%);
      pointer-events: none;
    }
  `,
})
export class ZonaMapa {
  readonly latitud = input.required<number>();
  readonly longitud = input.required<number>();

  private readonly sanitizer = inject(DomSanitizer);

  /**
   * URL del embed como recurso confiable. Al bypass del sanitizer solo
   * pasan números finitos (los valores llegan de la API): con cualquier
   * otra cosa se cae al estado sin mapa.
   */
  readonly mapaSrc = computed<SafeResourceUrl | null>(() => {
    const latitud = this.latitud();
    const longitud = this.longitud();
    if (!Number.isFinite(latitud) || !Number.isFinite(longitud)) return null;
    const url = `https://www.google.com/maps?ll=${latitud},${longitud}&z=${MAPA_ZOOM}&output=embed`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });
}
